import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useDebounce } from "@hooks";
import { Notification } from "@rio-cloud/rio-uikit";
import { TripSummary } from "@types";
import { parseIsoToBrDate } from "@utils";

import { formatFloatToStr } from "src/utils/formatFloatToStr";
import CardCountPerformance from "../../components/CardCountPerformance";
import PageContainer from "../../components/routes/PageContainer";
import TablePerformance from "../../components/TablePerformance";
import { fetchLastTripsByDrivers } from "../../services/trips";
import { filteredTrips } from "./components/filteredTrips";
import { mountDashboardTrips } from "./components/mountDashboardTrips";
import { ConfigPaginator } from "src/components/PaginatorSize";

const PAGE_SIZE = 10;
const PerformanceScreenList = () => {
  const navigate = useNavigate();

  const [trips, setTrips] = useState<TripSummary[]>([]);
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [backendInitialDate, setBackendInitialDate] = useState<string | undefined>(undefined);
  const [backendFinalDate, setBackendFinalDate] = useState<string | undefined>(undefined);
  const [searchValue, setSearchValue] = useState("");
  const [sort, setSort] = useState("desc");
  const [fieldSort, setFieldSort] = useState("createdAt");
  const [page, setPage] = useState(1);
  const [totalSize, setTotalSize] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const [dashboard, setDashboard] = useState({
    tripsInPeriod: 0,
    c02Emission: 0,
    plannedCo2Emission: 0,
    fuelConsumption: 0,
    plannedFuelConsumption: 0,
    distanceTraveled: 0,
    plannedDistanceTraveled: 0,
    timeTotal: 0,
    plannedTotalTime: 0,
  });

  useEffect(() => {
    loadTripsAndClean(1);
  }, []);

  useEffect(() => {
    loadMoreTrips();
  }, [searchValue, sort, fieldSort]);


  const loadTripsAndClean = async (page: number) => {
    const { trips, totalSize, result }: any = await searchTrips(page, searchValue, startDate, endDate);
    setTrips(trips);
    setTotalSize(totalSize);
    setDashboard(mountDashboardTrips(result));
    setBackendInitialDate(result?.initialDate)
    setBackendFinalDate(result?.finalDate)
  }

  const loadMoreTrips = async (newPage?: number) => {
    const res = await searchTrips(newPage || page, searchValue, startDate, endDate);
    setTrips(res.trips);
  }

  const searchTrips = async (page: number, searchValue: string, startDate?: string, endDate?: string) => {
    try {
      const {
        trips,
        totalTrips: backendTotalTrips,
        totalCo2Emission: totalCo2Emission,
        totalFuelConsumption: totalFuelConsumption,
        totalDistance: totalDistance,
        totalTime: totalTime,
        totalIdleTime: totalIdleTime,
        plannedFuelConsumption: plannedFuelConsumption,
        plannedCo2Emission: plannedCo2Emission,
        plannedDistance: plannedDistance,
        plannedTime: plannedTime,
        initialDate: backendStart,
        finalDate: backendEnd
      } = await fetchLastTripsByDrivers(searchValue, 0, page === 0 ? PAGE_SIZE : page * PAGE_SIZE, startDate, endDate, sort, fieldSort);

      const result = {
        totalTrips: backendTotalTrips,
        totalCo2Emission: totalCo2Emission,
        totalFuelConsumption: totalFuelConsumption,
        totalDistance: totalDistance,
        totalTime: totalTime,
        totalIdleTime: totalIdleTime,
        plannedFuelConsumption: plannedFuelConsumption,
        plannedCo2Emission: plannedCo2Emission,
        plannedDistance: plannedDistance,
        plannedTime: plannedTime,
        initialDate: backendStart,
        finalDate: backendEnd
      }
      setIsLoading(false);
      return { trips: trips, totalSize: backendTotalTrips, result: result };
    } catch (error) {
      Notification.error("Não foi possível buscar as últimas viagens.");
      setIsLoading(false);
      return { trips: [], totalSize: 0, result: {} };
    }
  }

  const handlePage = (config: ConfigPaginator) => {
    const nextPage = Math.round(config.next / PAGE_SIZE);
    setPage(nextPage);
    loadMoreTrips(nextPage);
  }

  const handleResetPage = () => {
    setPage(1);
    loadMoreTrips(1);
  }

  const handleSort = (dir: string, field: string) => {
    setSort(dir);
    setFieldSort(field);
  }

  const handleDownload = () => { };
  const handleShowTripDetails = (driverId: string, tripId: string) => {
    navigate("/routing/performance/list/trip", {
      state: {
        driverId,
        tripId,
        page,
      },
    });
  };
 const formatIntegerValueBr = (value: number): string => {
    return Math.round(value).toLocaleString("pt-BR");
  };
  const getPerformanceComparisonText = (
    planned: number,
    actual: number,
    unit: string
  ): string => {
    if (planned === actual) return `igual ao planejado`;
    const diff = formatIntegerValueBr(Math.abs(planned - actual));
    const suffix = planned > actual ? "a menos" : "a mais";
    return `${diff} ${unit} ${suffix} que o planejado`;
  };

  const getPerformanceStyle = (planned: number, actual: number) => {
    if (planned === actual) {
      return { borderColor: "bg-rating-4", description2Color: "text-color-black" };
    }

    const isPositive = planned > actual;
    return {
      borderColor: isPositive ? "bg-rating-4" : "bg-rating-1",
      description2Color: isPositive ? "text-color-success" : "text-color-danger"
    };
  };

  const handleSearchValueChange = async (text: string) => {
    setSearchValue(text);
    setPage(1);
    const searchValueInTrips = trips.filter(f => filteredTrips(text, f)).length > 0;
    if (text.length === 0) {
      const { trips, totalSize, result } = await searchTrips(0, "", startDate, endDate);
      setTrips(trips);
      setTotalSize(totalSize);
      setDashboard(mountDashboardTrips(result));
    } else if (!searchValueInTrips) {
      const { trips, totalSize } = await searchTrips(text.length >= 0 ? -1 : page, text, startDate, endDate);
      setTrips(trips);
      setTotalSize(totalSize);
    }
  }

  const handleDateFilterChange = async (start?: string, end?: string) => {
    // filtro por data
    setStartDate(start);
    setEndDate(end);
    setPage(1);
    const { trips, totalSize, result } = await searchTrips(0, "", start, end);
    setTrips(trips);
    setTotalSize(totalSize);
    setDashboard(mountDashboardTrips(result));
  };

  return (
    <PageContainer>
      <div className="display-flex gap-20">
        <CardCountPerformance
          borderColor="bg-primary"
          title="VIAGENS NO PERÍODO"
          description={"Ultimos 30 dias"}
          value={dashboard.tripsInPeriod + " viagens"}
          description2={
            startDate && endDate
              ? `${parseIsoToBrDate(startDate)} a ${parseIsoToBrDate(endDate)}`
              : backendInitialDate && backendFinalDate
                ? `${parseIsoToBrDate(backendInitialDate)} a ${parseIsoToBrDate(backendFinalDate)}`
                : ""
          }
          icon={"rioglyph rioglyph-calendar"}
          isFirst={true}
        />

        {(() => {
          const style = getPerformanceStyle(dashboard.plannedCo2Emission, dashboard.c02Emission);
          return (
            <CardCountPerformance
              borderColor={style.borderColor}
              description2Color={style.description2Color}
              title="EMISSÃO CO2"
              value={formatFloatToStr(dashboard.c02Emission) + " kg CO2"}
              icon={"rioglyph rioglyph-globe"}
            />
          );
        })()}
        {(() => {
          const style = getPerformanceStyle(dashboard.plannedFuelConsumption, dashboard.fuelConsumption);
          return (
            <CardCountPerformance
              borderColor={style.borderColor}
              description2Color={style.description2Color}
              title="CONSUMO COMBUSTÍVEL"
              value={formatFloatToStr(dashboard.fuelConsumption) + " litros"}
              icon={"rioglyph rioglyph-filling-station"}
            />
          );
        })()}
        {(() => {
          const style = getPerformanceStyle(dashboard.plannedDistanceTraveled, dashboard.distanceTraveled);
          return (
            <CardCountPerformance
              borderColor={style.borderColor}
              description2Color={style.description2Color}
              title="DISTÂNCIA PERCORRIDA"
              description={`${formatIntegerValueBr(dashboard.plannedDistanceTraveled)} km`}
              value={`${formatIntegerValueBr(dashboard.distanceTraveled)} km`}
              description2={getPerformanceComparisonText(
                dashboard.plannedDistanceTraveled,
                dashboard.distanceTraveled,
                "km"
              )}
              icon={"rioglyph rioglyph-road"}
            />
          );
        })()}
        {(() => {
          const style = getPerformanceStyle(dashboard.plannedTotalTime, dashboard.timeTotal);
          return (
            <CardCountPerformance
              borderColor={style.borderColor}
              description2Color={style.description2Color}
              title="TEMPO TOTAL"
              description={`${Math.round(dashboard.plannedTotalTime)} horas`}
              value={`${Math.round(dashboard.timeTotal)} horas`}
              description2={getPerformanceComparisonText(
                dashboard.plannedTotalTime,
                dashboard.timeTotal,
                "horas"
              )}
              icon={"rioglyph rioglyph-time"}
            />
          );
        })()}
      </div>
      <TablePerformance
        isLoading={isLoading}
        pageSize={PAGE_SIZE}
        viewType="TABLE"
        size={page * PAGE_SIZE}
        totalSize={totalSize}
        handleResetPage={handleResetPage}
        trips={trips}
        searchValue={searchValue}
        handleDownload={handleDownload}
        handleSearchValueChange={handleSearchValueChange}
        handlePage={handlePage}
        handleSort={handleSort}
        handleShowTripDetail={handleShowTripDetails}
        onDateFilterChange={handleDateFilterChange}
      />
    </PageContainer>
  );
};

export default PerformanceScreenList;